import { Base64 } from 'js-base64'
import { hexToBase64 } from './util'
import { sm2 } from 'sm-crypto-v2'

/**
 * SM2加密工具类
 * 实现C1C3C2标准格式的加密解密
 */
export class SM2CryptoUtils {
  /**
   * 生成SM2密钥对
   * @returns 包含公钥和私钥的对象
   */
  public static generateKeyPair(): { publicKey: string; privateKey: string } {
    try {
      return sm2.generateKeyPairHex()
    }
    catch (error) {
      console.error('生成SM2密钥对失败', error)
      throw new Error('生成SM2密钥对失败')
    }
  }

  /**
   * 验证公钥是否有效
   * @param publicKey 公钥（十六进制字符串）
   * @returns 是否有效
   */
  public static verifyPublicKey(publicKey: string): boolean {
    try {
      return sm2.verifyPublicKey(publicKey)
    }
    catch (error) {
      console.error('验证SM2公钥失败', error)
      return false
    }
  }

  /**
   * SM2加密（C1C3C2格式）
   *
   * @param data 待加密数据
   * @param publicKey 公钥（十六进制字符串）
   * @param mode 加密模式，1 - C1C3C2，0 - C1C2C3，默认为1
   * @returns 加密后的十六进制字符串
   */
  public static encrypt(data: string | Uint8Array, publicKey: string, mode: 0 | 1 = 1): string {
    try {
      const encryptedHex = sm2.doEncrypt(data, publicKey, mode)
      // 验证和清理十六进制字符串，确保只包含有效的十六进制字符
      const cleanHex = this.cleanHexString(encryptedHex)
      return hexToBase64(`04${cleanHex}`)
    }
    catch (error) {
      console.error('SM2加密失败', error)
      throw new Error('SM2加密失败')
    }
  }

  /**
   * SM2解密（C1C3C2格式）
   *
   * @param encryptedData 加密数据（十六进制字符串）
   * @param privateKey 私钥（十六进制字符串）
   * @param mode 加密模式，1 - C1C3C2，0 - C1C2C3，默认为1
   * @returns 解密后的字符串
   */
  public static decrypt(encryptedData: string, privateKey: string, mode: 0 | 1 = 1): string {
    try {
      // 清理加密数据中的非法字符
      const cleanEncryptedData = this.cleanHexString(encryptedData)
      // 使用兼容模式解密
      const decryptedData = sm2.doDecrypt(cleanEncryptedData, privateKey, mode, { output: 'text' } as any)
      return typeof decryptedData === 'string' ? decryptedData : new TextDecoder().decode(decryptedData)
    }
    catch (error) {
      console.error('SM2解密失败', error)
      throw new Error('SM2解密失败')
    }
  }

  /**
   * 使用公钥加密数据，返回base64编码的加密结果（C1C3C2格式）
   * @param data 待加密的字符串
   * @param publicKey 公钥（十六进制字符串）
   * @returns base64编码的加密结果
   */
  public static encryptToBase64(data: string, publicKey: string): string {
    try {
      // 加密数据
      const encryptedHex = this.encrypt(data, publicKey)
      // 转换为Base64
      return Base64.encode(encryptedHex)
    }
    catch (error) {
      console.error('SM2加密失败', error)
      throw new Error('SM2加密失败')
    }
  }

  /**
   * 使用私钥解密base64编码的加密数据（C1C3C2格式）
   * @param encryptedDataBase64 base64编码的加密数据
   * @param privateKey 私钥（十六进制字符串）
   * @returns 解密后的字符串
   */
  public static decryptFromBase64(encryptedDataBase64: string, privateKey: string): string {
    try {
      // 解码Base64为十六进制字符串
      const encryptedHex = Base64.decode(encryptedDataBase64)
      // 解密数据
      return this.decrypt(encryptedHex, privateKey)
    }
    catch (error) {
      console.error('SM2解密失败', error)
      throw new Error('SM2解密失败')
    }
  }

  /**
   * 使用私钥解密十六进制格式的加密数据（C1C3C2格式）
   *
   * @param encryptedDataHex 十六进制格式的加密数据
   * @param privateKey 私钥（十六进制字符串）
   * @returns 解密后的字符串
   */
  public static decryptFromHex(encryptedDataHex: string, privateKey: string): string {
    try {
      return this.decrypt(encryptedDataHex, privateKey)
    }
    catch (error) {
      console.error('SM2解密失败', error)
      throw new Error('SM2解密失败')
    }
  }

  /**
   * SM2签名
   *
   * @param message 待签名的消息
   * @param privateKey 私钥（十六进制字符串）
   * @returns 签名结果（DER编码的十六进制字符串）
   */
  public static sign(message: string, privateKey: string): string {
    try {
      // sm2签名默认做了sm3的hash处理
      const signature = sm2.doSignature(message, privateKey)
      // 清理签名结果中的非法字符
      return this.cleanHexString(signature)
    }
    catch (error) {
      console.error('SM2签名失败', error)
      throw new Error('SM2签名失败')
    }
  }

  /**
   * SM2验签
   *
   * @param message 原始消息
   * @param signature 签名（DER编码的十六进制字符串）
   * @param publicKey 公钥（十六进制字符串）
   * @returns 验签结果
   */
  public static verify(message: string, signature: string, publicKey: string): boolean {
    try {
      // 清理签名字符串中的非法字符
      const cleanSignature = this.cleanHexString(signature)
      return sm2.doVerifySignature(message, cleanSignature, publicKey)
    }
    catch (error) {
      console.error('SM2验签失败', error)
      return false
    }
  }

  /**
   * 清理十六进制字符串，移除非法字符
   * @param hexString 原始十六进制字符串
   * @returns 清理后的十六进制字符串
   */
  private static cleanHexString(hexString: string): string {
    // 只保留有效的十六进制字符（0-9, a-f, A-F）
    return hexString.replace(/[^0-9a-fA-F]/g, '')
  }
}
